// Landing page content for each new Cloudbase module.
// Icons referenced here are resolved at render time in ModulePage via lucide-react.

export const moduleLandingData = {
  sales: {
    slug: 'ventas',
    name: 'Ventas',
    tagline: 'Módulo de Ventas',
    icon: 'ShoppingCart',
    accent: 'green',
    hero: {
      title: 'Cotizaciones, Pedidos y Facturación en un Solo Flujo',
      subtitle:
        'El módulo de Ventas de Cloudbase conecta el pipeline comercial con la operación: genera cotizaciones profesionales, convierte a pedidos y factura electrónicamente sin volver a ingresar datos.',
      bullets: [
        'Cotizaciones con plantillas personalizables',
        'Pedidos, despachos y facturación integrados',
        'Pipeline comercial con forecast automático',
        'Integración nativa con SII Chile (DTE)'
      ]
    },
    seo: {
      title: 'Ventas - Cotizaciones, Pedidos y Facturación | Cloudbase',
      description:
        'Módulo de Ventas Cloudbase: cotizaciones, pedidos, despachos y facturación electrónica integrados. Forecast automático y pipeline comercial.',
      keywords: 'software ventas, cotizaciones, pedidos, facturación electrónica, pipeline comercial, CRM ventas, Cloudbase'
    },
    features: [
      { icon: 'FileText', title: 'Cotizaciones inteligentes', description: 'Plantillas, descuentos escalonados y aprobaciones con flujo configurable.' },
      { icon: 'ShoppingCart', title: 'Gestión de pedidos', description: 'Convierte cotizaciones en pedidos con un clic y sincroniza con inventario.' },
      { icon: 'FileCheck', title: 'Facturación DTE', description: 'Emite facturas, boletas y notas integradas con SII Chile en tiempo real.' },
      { icon: 'TrendingUp', title: 'Forecast automático', description: 'Proyección de ingresos por etapa, vendedor y producto.' },
      { icon: 'Users', title: 'Multi-vendedor y comisiones', description: 'Calcula comisiones por reglas configurables y genera reportes mensuales.' },
      { icon: 'Zap', title: 'Integración total', description: 'Se conecta con CRM, Inventario, Contabilidad y Logística nativamente.' }
    ],
    howItWorks: [
      { title: 'Cotiza', description: 'Crea propuestas en segundos con catálogo, descuentos y términos predefinidos.' },
      { title: 'Vende', description: 'Aprueba, convierte a pedido y despacha coordinando con stock y logística.' },
      { title: 'Factura', description: 'Emite DTE, recibe el pago y cierra el ciclo sin re-digitar información.' }
    ]
  },
  inventory: {
    slug: 'inventario',
    name: 'Inventario',
    tagline: 'Control de Stock y Almacenes',
    icon: 'Package',
    accent: 'orange',
    hero: {
      title: 'Control Total de tu Stock, en Todos tus Almacenes',
      subtitle:
        'Visibilidad en tiempo real de entradas, salidas y traslados. Kardex, lotes, series, vencimientos y alertas de stock mínimo en una sola plataforma.',
      bullets: [
        'Multi-almacén y multi-bodega',
        'Kardex valorizado automático',
        'Lotes, series y fechas de vencimiento',
        'Conteos cíclicos y ajustes con trazabilidad'
      ]
    },
    seo: {
      title: 'Inventario - Control de Stock y Almacenes | Cloudbase',
      description:
        'Módulo de Inventario Cloudbase: multi-almacén, kardex valorizado, lotes, series y alertas de stock. Integrado con Ventas y Logística.',
      keywords: 'software inventario, control de stock, kardex, multi almacén, WMS, lotes y series, Cloudbase'
    },
    features: [
      { icon: 'Package', title: 'Multi-almacén', description: 'Gestiona bodegas, ubicaciones y zonas con reglas específicas por almacén.' },
      { icon: 'BarChart3', title: 'Kardex valorizado', description: 'Método FIFO, LIFO o promedio ponderado, con historial completo por SKU.' },
      { icon: 'ScanBarcode', title: 'Códigos de barra y QR', description: 'Recepción, picking y conteo con app móvil y lectores.' },
      { icon: 'Bell', title: 'Alertas de stock', description: 'Mínimos, máximos y puntos de reorden con notificación automática.' },
      { icon: 'ClipboardList', title: 'Conteos cíclicos', description: 'Inventarios programados, ajustes con aprobación y trazabilidad.' },
      { icon: 'RefreshCw', title: 'Traslados entre bodegas', description: 'Documenta y controla movimientos internos con stock en tránsito.' }
    ],
    howItWorks: [
      { title: 'Recibe', description: 'Ingresa mercancía contra orden de compra con validación por escaneo.' },
      { title: 'Controla', description: 'Monitorea stock en tiempo real, con alertas y kardex valorizado.' },
      { title: 'Despacha', description: 'Picking optimizado y salida sincronizada con Ventas y Logística.' }
    ]
  },
  accounting: {
    slug: 'contabilidad',
    name: 'Contabilidad',
    tagline: 'Plan de Cuentas, Asientos y Balances',
    icon: 'Calculator',
    accent: 'red',
    hero: {
      title: 'Contabilidad Chilena Automatizada, sin Hojas de Cálculo',
      subtitle:
        'Plan de cuentas, asientos automáticos desde Ventas, Compras y Nómina, libros legales, F29 y estados financieros en tiempo real.',
      bullets: [
        'Plan de cuentas 100% configurable',
        'Asientos automáticos desde otros módulos',
        'Libros de Compra, Venta y Mayor',
        'Balance y Estado de Resultados al día'
      ]
    },
    seo: {
      title: 'Contabilidad - Plan de Cuentas y Balances | Cloudbase',
      description:
        'Módulo de Contabilidad Cloudbase: plan de cuentas, asientos automáticos, libros legales, F29 y estados financieros en tiempo real.',
      keywords: 'software contabilidad Chile, plan de cuentas, asientos contables, F29, libros contables, estados financieros, Cloudbase'
    },
    features: [
      { icon: 'BookOpen', title: 'Plan de cuentas flexible', description: 'Árbol jerárquico personalizable con centros de costo y dimensiones.' },
      { icon: 'Repeat', title: 'Asientos automáticos', description: 'Cada venta, compra, nómina o pago genera su asiento sin intervención.' },
      { icon: 'FileText', title: 'Libros legales', description: 'Libro Diario, Mayor, Compras y Ventas listos para fiscalización.' },
      { icon: 'PieChart', title: 'Estados financieros', description: 'Balance General y Estado de Resultados siempre actualizados.' },
      { icon: 'Receipt', title: 'F29 y F22', description: 'Pre-llenado de declaraciones mensuales y anuales al SII.' },
      { icon: 'Lock', title: 'Cierre de período', description: 'Bloqueo de períodos contables con control de acceso por usuario.' }
    ],
    howItWorks: [
      { title: 'Configura', description: 'Define plan de cuentas, centros de costo y reglas de contabilización.' },
      { title: 'Registra', description: 'Los módulos operativos generan asientos automáticos y trazables.' },
      { title: 'Reporta', description: 'Balances, libros e informes tributarios disponibles en un clic.' }
    ]
  },
  hr: {
    slug: 'rrhh',
    name: 'RRHH',
    tagline: 'Recursos Humanos, Nóminas y Asistencia',
    icon: 'UserCog',
    accent: 'green',
    hero: {
      title: 'Gestiona tu Capital Humano en una Plataforma Unificada',
      subtitle:
        'Ficha laboral, contratos, liquidaciones de sueldo, vacaciones, ausentismo y portal del colaborador. Integrado con Control de Asistencia y Contabilidad.',
      bullets: [
        'Liquidaciones de sueldo según legislación chilena',
        'Contratos y anexos digitales con firma electrónica',
        'Portal del colaborador con solicitudes',
        'Integración con Previred y Dirección del Trabajo'
      ]
    },
    seo: {
      title: 'RRHH - Recursos Humanos y Nóminas | Cloudbase',
      description:
        'Módulo de RRHH Cloudbase: ficha laboral, contratos, liquidaciones, vacaciones y portal del colaborador. Cumple con normativa chilena.',
      keywords: 'software RRHH, recursos humanos, nómina electrónica Chile, liquidación de sueldo, Previred, portal colaborador, Cloudbase'
    },
    features: [
      { icon: 'UserCog', title: 'Ficha del colaborador', description: 'Datos personales, contratos, documentos y historial laboral centralizado.' },
      { icon: 'FileText', title: 'Contratos digitales', description: 'Generación, firma electrónica y archivo con trazabilidad legal.' },
      { icon: 'DollarSign', title: 'Liquidaciones de sueldo', description: 'Cálculo automático con haberes, descuentos y aportes previsionales.' },
      { icon: 'Calendar', title: 'Vacaciones y permisos', description: 'Solicitudes, aprobaciones y saldo de días en el portal.' },
      { icon: 'Briefcase', title: 'Previred y DT', description: 'Generación de archivos para Previred y cumplimiento DT.' },
      { icon: 'Users', title: 'Portal del colaborador', description: 'Cada empleado accede a sus documentos, liquidaciones y solicitudes.' }
    ],
    howItWorks: [
      { title: 'Contrata', description: 'Crea ficha, contrato digital y onboarding en minutos.' },
      { title: 'Administra', description: 'Gestiona asistencia, vacaciones, permisos y evaluaciones.' },
      { title: 'Paga', description: 'Liquidaciones automáticas y transferencias masivas con contabilización.' }
    ]
  },
  projects: {
    slug: 'proyectos',
    name: 'Proyectos',
    tagline: 'Gestión de Proyectos estilo Jira',
    icon: 'FolderKanban',
    accent: 'purple',
    hero: {
      title: 'Planifica, Ejecuta y Entrega Proyectos con Visibilidad Total',
      subtitle:
        'Tableros Kanban, sprints ágiles, cronogramas Gantt y control de horas. Perfecto para equipos de desarrollo, implementación y servicios profesionales.',
      bullets: [
        'Tableros Kanban y Scrum',
        'Sprints con backlog y velocidad de equipo',
        'Cronogramas Gantt interactivos',
        'Control de horas y rentabilidad por proyecto'
      ]
    },
    seo: {
      title: 'Proyectos - Tareas, Sprints y Gantt estilo Jira | Cloudbase',
      description:
        'Módulo de Proyectos Cloudbase: tableros Kanban, sprints ágiles, cronogramas Gantt y control de horas. Alternativa integrada a Jira.',
      keywords: 'software gestión proyectos, alternativa Jira, Scrum, Kanban, Gantt, sprints, tareas, Cloudbase'
    },
    features: [
      { icon: 'FolderKanban', title: 'Tableros Kanban', description: 'Flujos visuales con columnas personalizables y límites WIP.' },
      { icon: 'Repeat', title: 'Sprints ágiles', description: 'Backlog, planificación, daily, review y retrospectivas integradas.' },
      { icon: 'GanttChart', title: 'Cronogramas Gantt', description: 'Dependencias, ruta crítica y línea base para entregas complejas.' },
      { icon: 'Clock', title: 'Control de horas', description: 'Timesheet por tarea, con aprobación y facturación al cliente.' },
      { icon: 'Users', title: 'Colaboración en tiempo real', description: 'Menciones, comentarios, adjuntos y notificaciones integradas.' },
      { icon: 'BarChart3', title: 'Rentabilidad por proyecto', description: 'Compara horas vendidas vs ejecutadas y márgenes en vivo.' }
    ],
    howItWorks: [
      { title: 'Planifica', description: 'Crea épicas, historias y tareas con estimaciones y dependencias.' },
      { title: 'Ejecuta', description: 'Trabaja en sprints con tableros y daily stand-ups integrados.' },
      { title: 'Mide', description: 'Reporta avance, horas y rentabilidad con dashboards en tiempo real.' }
    ]
  },
  helpdesk: {
    slug: 'helpdesk',
    name: 'Soporte',
    tagline: 'Tickets, Base de Conocimiento y Chat',
    icon: 'Headphones',
    accent: 'blue',
    hero: {
      title: 'Un Helpdesk Profesional para Sorprender a tus Clientes',
      subtitle:
        'Centraliza tickets de email, chat, WhatsApp y formularios web. SLA automáticos, base de conocimiento pública y satisfacción post-ticket con SurveyHub.',
      bullets: [
        'Multicanal: email, chat, WhatsApp, web',
        'SLA y escalamiento automáticos',
        'Base de conocimiento pública e interna',
        'Encuesta CSAT al cerrar cada ticket'
      ]
    },
    seo: {
      title: 'Soporte - Helpdesk y Base de Conocimiento | Cloudbase',
      description:
        'Módulo de Soporte Cloudbase: tickets multicanal, SLA automáticos, base de conocimiento y chat. Integrado con SurveyHub y CRM.',
      keywords: 'helpdesk, software soporte, tickets, base de conocimiento, SLA, chat soporte, mesa de ayuda, Cloudbase'
    },
    features: [
      { icon: 'Inbox', title: 'Bandeja unificada', description: 'Todos los canales en una vista: email, chat, WhatsApp y formularios.' },
      { icon: 'Timer', title: 'SLA y prioridades', description: 'Tiempos de respuesta y resolución configurables con alertas.' },
      { icon: 'BookOpen', title: 'Base de conocimiento', description: 'Artículos públicos e internos con buscador inteligente.' },
      { icon: 'MessageSquare', title: 'Chat en vivo', description: 'Widget embebido en tu web con respuestas automáticas.' },
      { icon: 'Bot', title: 'Automatizaciones', description: 'Reglas para asignar, clasificar y responder tickets frecuentes.' },
      { icon: 'Star', title: 'CSAT integrado', description: 'Al cerrar ticket, envía encuesta de satisfacción con SurveyHub.' }
    ],
    howItWorks: [
      { title: 'Recibe', description: 'Consolida tickets de todos los canales en una sola bandeja.' },
      { title: 'Resuelve', description: 'Asigna, responde y colabora con SLA y artículos sugeridos.' },
      { title: 'Mide', description: 'Analiza volumen, tiempos de respuesta y satisfacción del cliente.' }
    ]
  },
  attendance: {
    slug: 'control-asistencia',
    name: 'Control de Asistencia',
    tagline: 'Registro de Jornada Laboral según la DT',
    icon: 'Fingerprint',
    accent: 'orange',
    hero: {
      title: 'Cumple con la Dirección del Trabajo, sin Complicaciones',
      subtitle:
        'Registro de entrada y salida por huella, rostro, QR o app móvil. Cumple con el Reglamento de Control de Asistencia de la DT y genera el libro oficial.',
      bullets: [
        'Marcación biométrica, QR y app móvil',
        'Cumple con normativa DT vigente en Chile',
        'Cálculo automático de horas extra',
        'Reportes y libro de asistencia firmables'
      ]
    },
    seo: {
      title: 'Control de Asistencia - Normativa DT Chile | Cloudbase',
      description:
        'Módulo de Control de Asistencia Cloudbase: marcación biométrica, geolocalizada y app móvil. Cumple con Dirección del Trabajo (DT) de Chile.',
      keywords: 'control asistencia Chile, Dirección del Trabajo, DT, libro asistencia, marcación biométrica, jornada laboral, Cloudbase'
    },
    features: [
      { icon: 'Fingerprint', title: 'Biometría', description: 'Huella, reconocimiento facial y pines personales para cada colaborador.' },
      { icon: 'QrCode', title: 'QR y app móvil', description: 'Marcación desde smartphone con geocerca y verificación de ubicación.' },
      { icon: 'Clock', title: 'Horas extras automáticas', description: 'Cálculo según turno, día ordinario, sábado, domingo y festivos.' },
      { icon: 'ShieldCheck', title: 'Cumplimiento DT', description: 'Cumple con el Reglamento de Control de Asistencia y Jornada de Trabajo.' },
      { icon: 'FileText', title: 'Libro oficial', description: 'Genera libro firmable electrónicamente o exportable a PDF/Excel.' },
      { icon: 'Bell', title: 'Alertas de atrasos', description: 'Notificaciones automáticas a jefes directos y RRHH.' }
    ],
    howItWorks: [
      { title: 'Configura turnos', description: 'Define horarios, colaciones y excepciones por colaborador o equipo.' },
      { title: 'Registra marcas', description: 'Usa reloj biométrico, QR o app móvil con geocerca.' },
      { title: 'Exporta', description: 'Genera libro DT y alimenta automáticamente el cálculo de sueldos.' }
    ]
  },
  spaceshub: {
    slug: 'spaceshub',
    name: 'SpacesHub',
    tagline: 'Gestión Integral de Espacios y Reservas',
    icon: 'Building2',
    accent: 'cyan',
    hero: {
      title: 'La Torre de Control de tus Espacios Físicos',
      subtitle:
        'Reservas, ocupación, facturación y experiencia del usuario para coworks, oficinas, salas, canchas, gimnasios y cualquier espacio compartido.',
      bullets: [
        'Reservas online con calendario inteligente',
        'Mapas interactivos y planos por piso',
        'Facturación automática de membresías',
        'App móvil para miembros y visitantes'
      ]
    },
    seo: {
      title: 'SpacesHub - Gestión de Espacios y Reservas | Cloudbase',
      description:
        'SpacesHub Cloudbase: sistema integral de reservas, ocupación y facturación para coworks, oficinas, salas y espacios compartidos.',
      keywords: 'reserva de espacios, software coworking, gestión salas, ocupación oficinas, software reservas, SpacesHub, Cloudbase'
    },
    features: [
      { icon: 'Calendar', title: 'Reservas en tiempo real', description: 'Disponibilidad al instante con reglas por tipo de espacio y usuario.' },
      { icon: 'Map', title: 'Mapas interactivos', description: 'Planos por piso para elegir puesto, oficina o sala visualmente.' },
      { icon: 'CreditCard', title: 'Membresías y facturación', description: 'Ciclos recurrentes, consumos y facturación DTE automática.' },
      { icon: 'KeyRound', title: 'Control de acceso', description: 'Integración con QR, tarjetas y apertura remota de puertas.' },
      { icon: 'Smartphone', title: 'App para miembros', description: 'Reserva, paga y accede a amenities desde tu marca.' },
      { icon: 'BarChart3', title: 'Analítica de ocupación', description: 'Heatmaps de uso por hora, espacio y segmento.' }
    ],
    howItWorks: [
      { title: 'Configura', description: 'Crea espacios, tarifas y reglas de reserva a medida.' },
      { title: 'Opera', description: 'Miembros reservan, entran y pagan sin intervención manual.' },
      { title: 'Optimiza', description: 'Decide con data: ocupación, ingresos y satisfacción real.' }
    ]
  },
  logistics: {
    slug: 'logistica',
    name: 'Logística',
    tagline: 'Torre de Control de Operaciones Logísticas',
    icon: 'Truck',
    accent: 'orange',
    hero: {
      title: 'Despachos, Exportaciones y Rutas bajo un Solo Comando',
      subtitle:
        'Planifica rutas, gestiona despachos, controla exportaciones e inspecciones y entrega visibilidad total a tus clientes desde el pedido hasta la entrega.',
      bullets: [
        'Planificación y optimización de rutas',
        'Gestión de despachos y prueba de entrega',
        'Exportaciones con documentación aduanera',
        'Inspecciones digitales con checklist y fotos'
      ]
    },
    seo: {
      title: 'Logística - Despachos, Rutas y Exportaciones | Cloudbase',
      description:
        'Módulo de Logística Cloudbase: torre de control con planificación de rutas, despachos, exportaciones e inspecciones digitales.',
      keywords: 'software logística, planificación rutas, despachos, exportaciones, inspecciones, torre de control logística, Cloudbase'
    },
    features: [
      { icon: 'Map', title: 'Optimización de rutas', description: 'Algoritmos inteligentes de rutas por capacidad, ventanas y zonas.' },
      { icon: 'Truck', title: 'Despachos y POD', description: 'Prueba de entrega con foto, firma y geolocalización.' },
      { icon: 'Globe', title: 'Exportaciones', description: 'DUS, conocimientos de embarque y seguimiento por naviera.' },
      { icon: 'ClipboardCheck', title: 'Inspecciones digitales', description: 'Checklists configurables con evidencia fotográfica.' },
      { icon: 'Bell', title: 'Alertas y excepciones', description: 'Detección de retrasos, desviaciones y eventos críticos.' },
      { icon: 'Share2', title: 'Tracking al cliente', description: 'Página pública de seguimiento con ETA y notificaciones.' }
    ],
    howItWorks: [
      { title: 'Planifica', description: 'Arma rutas óptimas con cargas, ventanas y conductores disponibles.' },
      { title: 'Ejecuta', description: 'Los conductores operan desde la app con POD y eventos en vivo.' },
      { title: 'Controla', description: 'Torre de control con excepciones, KPIs y visibilidad al cliente.' }
    ]
  }
};
